/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import { serverBaseURL } from "common/settings";

function Authentication() {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const handleConfirmOpen = () => setConfirmOpen(true);
  const handleConfirmClose = () => setConfirmOpen(false);
  const [loadingOpen, setLoadingOpen] = useState(false);
  const handleLoadingClose = () => setLoadingOpen(false);
  const handleLoadingOpen = () => setLoadingOpen(true);
  const { getToken } = useKindeAuth();
  async function resetMFA() {
    const accessToken = await getToken();
    handleLoadingOpen();
    const deleteAccount = await (
      await fetch(`${serverBaseURL}reset_mfa`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      })
    ).json();
    handleLoadingClose();
    console.log(deleteAccount);
  }
  return (
    <Card id="2fa" sx={{ overflow: "visible" }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
        <MDTypography variant="h5">Two-factor authentication</MDTypography>
        <MDBadge variant="contained" color="success" badgeContent="enabled" container />
      </MDBox>
      <MDBox p={3}>
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loadingOpen}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <Modal
          open={confirmOpen}
          onClick={handleConfirmClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <MDBox
            p={5}
            position="absolute"
            color="white"
            bgColor="white"
            transform="translate(-50%, -50%)"
          >
            <MDTypography variant="h5">
              Would you like to change your two-factor authentication method? If your current
              authentication method is Email Verification, it can be changed to Authenticator. If
              your current authentication method is Authenticator, it can be changed to Email
              Verification. If you wish to proceed, our support team will send you a notification
              regarding the change within five business days.
            </MDTypography>
            <br />
            <Stack spacing={5} direction="row">
              <MDButton
                p={5}
                variant="gradient"
                color="dark"
                onClick={() => {
                  resetMFA();
                  handleConfirmClose();
                }}
              >
                Change Two-Factor Authentication Method
              </MDButton>
              <MDButton
                p={5}
                variant="gradient"
                color="dark"
                onClick={() => {
                  handleConfirmClose();
                }}
              >
                Cancel
              </MDButton>
            </Stack>
          </MDBox>
        </Modal>
        {/*}
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
        >
          <MDTypography variant="body2" color="text">
            Security keys
          </MDTypography>
          <MDBox
            display="flex"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            <MDBox mx={{ xs: 0, sm: 2 }} mb={{ xs: 1, sm: 0 }}>
              <MDTypography variant="button" color="text" fontWeight="regular">
                No Security keys
              </MDTypography>
            </MDBox>
            <MDButton variant="outlined" color="dark" size="small">
              add
            </MDButton>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
        >
          <MDTypography variant="body2" color="text">
            SMS number
          </MDTypography>
          <MDBox
            display="flex"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            <MDBox mx={{ xs: 0, sm: 2 }} mb={{ xs: 1, sm: 0 }}>
              <MDTypography variant="button" color="text" fontWeight="regular">
                +3012374423
              </MDTypography>
            </MDBox>
            <MDButton variant="outlined" color="dark" size="small">
              edit
            </MDButton>
          </MDBox>
        </MDBox>
        <Divider />
        {*/}
        <MDTypography variant="body2" color="text" fontWeight="bold">
          Transition Process
        </MDTypography>
        <MDBox component="ol" m={0} mt={0.5} pl={3}>
          <MDTypography component="li" variant="body2" color="text" lineHeight={1.6}>
            Click the <strong>Clear</strong> button.
          </MDTypography>
          <MDTypography component="li" variant="body2" color="text" lineHeight={1.6}>
            The following confirmation dialog will be displayed:
            <MDTypography
              component="div"
              variant="body2"
              color="text"
              fontWeight="bold"
              lineHeight={1.6}
              ml={1}
            >
              &quot;Would you like to change your two-factor authentication method? If your current
              authentication method is <em>Email Verification</em>, it can be changed to
              <em> Authenticator</em>. If your current authentication method is
              <em> Authenticator</em>, it can be changed to <em>Email Verification</em>. If you wish
              to proceed, our support team will send you a notification regarding the change within
              five business days.&quot;
            </MDTypography>
          </MDTypography>
          <MDTypography component="li" variant="body2" color="text" lineHeight={1.6}>
            Select one of the following options:
            <MDBox component="ul" m={0} pl={3}>
              <MDTypography
                component="li"
                variant="body2"
                color="text"
                fontWeight="bold"
                lineHeight={1.6}
              >
                Change Two-Factor Authentication Method
              </MDTypography>
              <MDTypography
                component="li"
                variant="body2"
                color="text"
                fontWeight="bold"
                lineHeight={1.6}
              >
                Cancel
              </MDTypography>
            </MDBox>
          </MDTypography>
        </MDBox>
        <MDBox mt={2}>
          <MDButton
            variant="gradient"
            color="error"
            size="small"
            onClick={() => handleConfirmOpen()}
          >
            Clear
          </MDButton>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default Authentication;
